import { useState, type ReactNode } from 'react';
import { MoreHorizontal, SlidersHorizontal } from 'lucide-react';
import type { ScanAgentCapabilities } from './scan-agent';

export interface ScanMoreProps {
  capabilities: ScanAgentCapabilities | null;
  driverWindow: boolean;
  disabled: boolean;
  onDriverWindow: (on: boolean) => void;
  onSettings: () => void;
}

/**
 * "More", the last pill of the row, for a scanner whose driver has its own window: scan through that window, or
 * open its settings window alone ("Scanner settings…", only when the scanner can hand its settings to Paige). Out
 * of the main path; nothing shows for a scanner without it.
 */
export function ScanMore(props: ScanMoreProps): ReactNode {
  const { capabilities, driverWindow, disabled, onDriverWindow, onSettings } = props;
  const [open, setOpen] = useState(false);
  if (!capabilities?.driverWindow) return null;
  return (
    <div className="paige-scan-more">
      <button
        type="button"
        className={`paige-scan-more-button${driverWindow ? ' is-on' : ''}`}
        aria-label="More"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(!open)}
      >
        <MoreHorizontal size={16} aria-hidden="true" />
      </button>
      {open && !disabled && (
        <MoreMenu
          driverWindow={driverWindow}
          settings={capabilities.driverSettings === true}
          onDriverWindow={onDriverWindow}
          onSettings={() => {
            setOpen(false);
            onSettings();
          }}
        />
      )}
    </div>
  );
}

function MoreMenu(m: {
  driverWindow: boolean;
  settings: boolean;
  onDriverWindow: (on: boolean) => void;
  onSettings: () => void;
}): ReactNode {
  return (
    <div className="paige-scan-more-menu" role="group" aria-label="More">
      <label className="paige-scan-toggle">
        <input
          type="checkbox"
          checked={m.driverWindow}
          onChange={(e) => m.onDriverWindow(e.target.checked)}
        />
        Use the scanner’s own window
      </label>
      {m.settings && (
        <button type="button" className="paige-scan-toggle" onClick={m.onSettings}>
          <SlidersHorizontal size={14} aria-hidden="true" />
          Scanner settings…
        </button>
      )}
    </div>
  );
}

/** One quiet line when the scanner's saved settings are put back before each scan, and the way to drop them. */
export function ScanSavedNote({
  capabilities,
  disabled,
  onReset,
}: {
  capabilities: ScanAgentCapabilities | null;
  disabled: boolean;
  onReset: () => void;
}): ReactNode {
  if (!capabilities?.savedSettings) return null;
  return (
    <p className="paige-scan-saved">
      Using your scanner’s saved settings ·{' '}
      <button type="button" className="paige-scan-quiet-link" disabled={disabled} onClick={onReset}>
        Reset
      </button>
    </p>
  );
}
